/**
 * help-feedback.ts — 波波 AI 边栏插件通用「帮助 & 反馈」组件
 *
 * 框架无关：一个 mountHelpFeedback() 调用即可。vanilla 插件直接调，
 * React 插件在 useEffect 里调。全部样式封在 Shadow DOM 里，绝不与
 * 宿主插件 CSS 冲突，6 个插件长得完全一致。
 *
 * 提供：常驻触发按钮 + 快速上手 + 一键进用户群 + 群二维码 + 首次自动引导。
 *
 * 维护约定：这是唯一权威源，改文案/群链接只改这里，再同步进各插件。
 */

export interface HelpFeedbackConfig {
  /** 插件名，显示在面板标题，如「邮箱同步」 */
  pluginName: string;
  /** 快速上手步骤，2–4 条，每条一句话 */
  quickStart: string[];
  /** 飞书用户群 applink */
  groupLink: string;
  /** 群二维码 data URI（PNG/SVG）；给手机扫码进群 */
  qrDataUri: string;
  /** 主题强调色，默认 apibobo 赤陶色 */
  accent?: string;
  /** 触发按钮挂载容器；不传则右下角浮动 */
  mountInto?: HTMLElement | null;
  /** 纯图标圆钮（不带「帮助」文字），适合空间紧张的 header */
  compact?: boolean;
  /** 首次打开插件时自动弹一次，默认 true */
  autoOpenFirstRun?: boolean;
  /** localStorage 命名空间，用于「首次引导只弹一次」，默认按 pluginName */
  storageKey?: string;
}

const ICON_LIFEBUOY = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.4"/><path d="M4.9 4.9l4.1 4.1M15 15l4.1 4.1M19.1 4.9L15 9M9 15l-4.1 4.1"/></svg>`;
const ICON_CLOSE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>`;
const ICON_GROUP = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;

function shade(hex: string, amount: number): string {
  const m = hex.replace('#', '');
  const n = parseInt(m.length === 3 ? m.split('').map((c) => c + c).join('') : m, 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  r = Math.max(0, Math.min(255, Math.round(r + (amount < 0 ? r : 255 - r) * amount)));
  g = Math.max(0, Math.min(255, Math.round(g + (amount < 0 ? g : 255 - g) * amount)));
  b = Math.max(0, Math.min(255, Math.round(b + (amount < 0 ? b : 255 - b) * amount)));
  return `rgb(${r}, ${g}, ${b})`;
}

export function mountHelpFeedback(config: HelpFeedbackConfig): { open: () => void; destroy: () => void } {
  const accent = config.accent || '#c26442';
  const accentDark = shade(accent, -0.22);
  const accentSoftBg = shade(accent, 0.9);
  const storeKey = `bobo_help_seen_${config.storageKey || config.pluginName}`;

  // 模态框：独立 shadow host 挂 body
  const host = document.createElement('div');
  host.setAttribute('data-bobo-help', '');
  const root = host.attachShadow({ mode: 'open' });
  document.body.appendChild(host);

  // 触发按钮：自己一个 shadow host，这样无论塞进宿主 header 还是浮动，
  // 样式都跟着走（放进 light DOM 会丢掉模态 shadow 里的 CSS）。
  const triggerHost = document.createElement('span');
  triggerHost.setAttribute('data-bobo-help-trigger', '');
  const troot = triggerHost.attachShadow({ mode: 'open' });
  troot.innerHTML = `
    <style>
      * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Segoe UI", sans-serif; }
      .trigger {
        display: inline-flex; align-items: center; gap: 6px;
        height: 30px; padding: 0 12px 0 10px;
        border: 1px solid ${shade(accent, 0.55)}; border-radius: 999px;
        background: ${accentSoftBg}; color: ${accentDark};
        font-size: 12.5px; font-weight: 600; letter-spacing: .01em; line-height: 1;
        cursor: pointer; user-select: none; white-space: nowrap;
        transition: background .16s ease, box-shadow .16s ease, transform .16s ease;
      }
      .trigger:hover { background: ${shade(accent, 0.82)}; box-shadow: 0 2px 10px ${shade(accent, 0.2)}66; }
      .trigger:active { transform: translateY(1px); }
      .trigger svg { width: 15px; height: 15px; flex: none; }
      /* compact：纯图标圆钮，header 空间紧张时用 */
      :host(.compact) .trigger { width: 30px; height: 30px; padding: 0; border-radius: 50%; justify-content: center; gap: 0; }
      :host(.compact) .trigger .label { display: none; }
      :host(.floating) .trigger {
        height: 38px; padding: 0 15px 0 13px; font-size: 13px;
        box-shadow: 0 6px 22px rgba(60,40,20,.18);
      }
      .dot {
        width: 7px; height: 7px; border-radius: 50%; flex: none;
        background: ${accent}; box-shadow: 0 0 0 3px ${shade(accent, 0.75)}; margin-left: 1px;
      }
      .dot[hidden] { display: none; }
    </style>
    <button class="trigger" type="button" aria-label="帮助与反馈">
      ${ICON_LIFEBUOY}<span class="label">帮助</span><span class="dot" hidden></span>
    </button>`;

  const steps = config.quickStart
    .map(
      (s, i) => `
      <li class="qs-item">
        <span class="qs-num">${i + 1}</span>
        <span class="qs-text">${s}</span>
      </li>`
    )
    .join('');

  root.innerHTML = `
    <style>
      :host { all: initial; }
      * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Segoe UI", sans-serif; }

      .trigger {
        display: inline-flex; align-items: center; gap: 6px;
        height: 30px; padding: 0 12px 0 10px;
        border: 1px solid ${shade(accent, 0.55)}; border-radius: 999px;
        background: ${accentSoftBg}; color: ${accentDark};
        font-size: 12.5px; font-weight: 600; letter-spacing: .01em;
        cursor: pointer; user-select: none; white-space: nowrap;
        transition: background .16s ease, box-shadow .16s ease, transform .16s ease;
      }
      .trigger:hover { background: ${shade(accent, 0.82)}; box-shadow: 0 2px 10px ${shade(accent, 0.2)}66; }
      .trigger:active { transform: translateY(1px); }
      .trigger svg { width: 15px; height: 15px; }
      .trigger.floating {
        position: fixed; right: 16px; bottom: 16px; z-index: 2147483000;
        height: 38px; padding: 0 15px 0 13px; font-size: 13px;
        box-shadow: 0 6px 22px rgba(60,40,20,.18);
      }
      .trigger .dot {
        width: 7px; height: 7px; border-radius: 50%;
        background: ${accent}; box-shadow: 0 0 0 3px ${shade(accent, 0.75)};
        margin-left: 1px;
      }

      .overlay {
        position: fixed; inset: 0; z-index: 2147483600;
        display: flex; align-items: flex-end; justify-content: center;
        background: rgba(35, 24, 14, 0); backdrop-filter: blur(0px);
        opacity: 0; pointer-events: none;
        transition: opacity .2s ease, background .2s ease, backdrop-filter .2s ease;
      }
      .overlay.open { opacity: 1; pointer-events: auto; background: rgba(35, 24, 14, .34); backdrop-filter: blur(2px); }

      .sheet {
        width: 100%; max-width: 380px; margin: 0 12px 0;
        background: linear-gradient(180deg, #fffdf9 0%, #fbf6ec 100%);
        border: 1px solid #efe6d4; border-bottom: none;
        border-radius: 20px 20px 0 0;
        box-shadow: 0 -8px 40px rgba(60,40,20,.22);
        transform: translateY(24px); opacity: .4;
        transition: transform .26s cubic-bezier(.2,.9,.3,1), opacity .2s ease;
        overflow: hidden; max-height: 88vh; display: flex; flex-direction: column;
      }
      .overlay.open .sheet { transform: translateY(0); opacity: 1; }
      @media (min-width: 480px) {
        .overlay { align-items: center; }
        .sheet { border-radius: 20px; border-bottom: 1px solid #efe6d4; margin: 0; }
      }

      .head {
        display: flex; align-items: center; gap: 10px;
        padding: 16px 16px 12px; position: relative;
      }
      .head-icon {
        width: 34px; height: 34px; border-radius: 10px; flex: none;
        display: grid; place-items: center; color: #fff;
        background: linear-gradient(150deg, ${accent} 0%, ${accentDark} 100%);
        box-shadow: 0 3px 10px ${shade(accent, 0.3)}66;
      }
      .head-icon svg { width: 19px; height: 19px; }
      .head-titles { display: flex; flex-direction: column; line-height: 1.25; }
      .head-title { font-size: 15px; font-weight: 700; color: #3a2c1e; }
      .head-sub { font-size: 11.5px; color: #a08a6d; margin-top: 1px; }
      .close {
        margin-left: auto; width: 28px; height: 28px; border-radius: 8px;
        border: none; background: transparent; color: #b09a7c; cursor: pointer;
        display: grid; place-items: center; transition: background .15s ease, color .15s ease;
      }
      .close:hover { background: #f0e7d6; color: #6b573c; }
      .close svg { width: 16px; height: 16px; }

      .body { padding: 4px 16px 16px; overflow-y: auto; }

      .section-label {
        font-size: 11px; font-weight: 700; letter-spacing: .06em;
        color: #b39a76; text-transform: none; margin: 10px 2px 8px;
      }

      .qs-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
      .qs-item {
        display: flex; align-items: flex-start; gap: 10px;
        padding: 10px 12px; border-radius: 12px;
        background: #fffdf8; border: 1px solid #f0e7d5;
      }
      .qs-num {
        flex: none; width: 20px; height: 20px; border-radius: 50%;
        display: grid; place-items: center; margin-top: 1px;
        font-size: 11.5px; font-weight: 700; color: #fff;
        background: linear-gradient(150deg, ${accent} 0%, ${accentDark} 100%);
      }
      .qs-text { font-size: 13px; line-height: 1.5; color: #4a3c2c; }
      .qs-text b { color: ${accentDark}; font-weight: 700; }

      .group-card {
        margin-top: 12px; padding: 14px; border-radius: 14px;
        background: ${accentSoftBg}; border: 1px solid ${shade(accent, 0.7)};
        display: flex; align-items: center; gap: 14px;
      }
      .qr { flex: none; width: 78px; height: 78px; border-radius: 10px; background: #fff; padding: 5px; border: 1px solid ${shade(accent, 0.7)}; }
      .qr img { width: 100%; height: 100%; display: block; image-rendering: pixelated; }
      .group-info { flex: 1; min-width: 0; }
      .group-info-title { font-size: 13.5px; font-weight: 700; color: #4a3320; }
      .group-info-sub { font-size: 11.5px; color: #9c8161; margin-top: 2px; line-height: 1.45; }

      .cta {
        margin-top: 12px; width: 100%; height: 44px;
        display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        border: none; border-radius: 12px; cursor: pointer;
        color: #fff; font-size: 14px; font-weight: 700; letter-spacing: .02em;
        background: linear-gradient(150deg, ${accent} 0%, ${accentDark} 100%);
        box-shadow: 0 4px 16px ${shade(accent, 0.25)}80;
        transition: filter .15s ease, transform .12s ease;
      }
      .cta:hover { filter: brightness(1.05); }
      .cta:active { transform: translateY(1px); }
      .cta svg { width: 17px; height: 17px; }

      .foot { text-align: center; font-size: 11px; color: #bcab90; margin: 12px 0 2px; }
    </style>

    <div class="overlay" role="dialog" aria-modal="true" aria-label="帮助与反馈">
      <div class="sheet">
        <div class="head">
          <div class="head-icon">${ICON_LIFEBUOY}</div>
          <div class="head-titles">
            <span class="head-title">${config.pluginName}</span>
            <span class="head-sub">使用帮助 & 反馈</span>
          </div>
          <button class="close" type="button" aria-label="关闭">${ICON_CLOSE}</button>
        </div>
        <div class="body">
          <div class="section-label">快速上手</div>
          <ul class="qs-list">${steps}</ul>

          <div class="section-label">有问题？随时进群反馈</div>
          <div class="group-card">
            <div class="qr"><img src="${config.qrDataUri}" alt="用户群二维码" /></div>
            <div class="group-info">
              <div class="group-info-title">波波 AI 用户群</div>
              <div class="group-info-sub">用法答疑、需求反馈、Bug 上报，作者本人在群里</div>
            </div>
          </div>
          <button class="cta" type="button">${ICON_GROUP}<span>一键加入用户群</span></button>

          <div class="foot">波波 AI · 让多维表格更好用</div>
        </div>
      </div>
    </div>
  `;

  const trigger = troot.querySelector('.trigger') as HTMLButtonElement;
  const dot = troot.querySelector('.dot') as HTMLElement;
  const overlay = root.querySelector('.overlay') as HTMLElement;
  const sheet = root.querySelector('.sheet') as HTMLElement;
  const closeBtn = root.querySelector('.close') as HTMLButtonElement;
  const cta = root.querySelector('.cta') as HTMLButtonElement;

  // 触发按钮定位：给了容器就内联进去，否则右下角浮动
  if (config.compact) triggerHost.classList.add('compact');
  if (config.mountInto) {
    config.mountInto.appendChild(triggerHost);
  } else {
    triggerHost.classList.add('floating');
    triggerHost.style.cssText = 'position:fixed;right:16px;bottom:16px;z-index:2147483000';
    document.body.appendChild(triggerHost);
  }

  const seen = (() => { try { return localStorage.getItem(storeKey) === '1'; } catch { return false; } })();
  const markSeen = () => { try { localStorage.setItem(storeKey, '1'); } catch { /* ignore */ } };

  let open = false;
  function setOpen(v: boolean) {
    open = v;
    overlay.classList.toggle('open', v);
    if (v) { markSeen(); dot.hidden = true; }
  }

  trigger.addEventListener('click', () => setOpen(true));
  closeBtn.addEventListener('click', () => setOpen(false));
  overlay.addEventListener('click', (e) => { if (e.target === overlay) setOpen(false); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && open) setOpen(false); });
  cta.addEventListener('click', () => {
    try { window.open(config.groupLink, '_blank', 'noopener'); } catch { location.href = config.groupLink; }
  });
  // 阻止面板内点击冒泡到 overlay 关闭
  sheet.addEventListener('click', (e) => e.stopPropagation());

  // 首次引导：没看过就自动弹一次；否则触发按钮点一下小红点提示
  if (!seen) {
    if (config.autoOpenFirstRun !== false) {
      setTimeout(() => setOpen(true), 900);
    } else {
      dot.hidden = false;
    }
  }

  return {
    open: () => setOpen(true),
    destroy: () => { try { host.remove(); triggerHost.remove(); } catch { /* ignore */ } }
  };
}
